import { useMemo } from 'react';

import { useStores } from '../../../stores/useStores';
import type { MetagameWidgetDataType, MetagameWidgetType, WidgetEntry } from '../types';

type MetagameWidgetsType = Record<MetagameWidgetType, MetagameWidgetDataType[]>;

export function useMetagameWidgets() {
  const { hammersStore, puzzleStore, trailStore, plinkoStore, rouletteStore } = useStores();

  const { hammersEvent } = hammersStore;
  const { puzzleEvent } = puzzleStore;
  const { trailEvent } = trailStore;
  const { plinkoEvent } = plinkoStore;
  const { rouletteEvent } = rouletteStore;

  const metagameWidgets: MetagameWidgetsType = useMemo(() => {
    let hammers: MetagameWidgetDataType[] = [];
    if (hammersEvent && !hammersEvent.isCrownSlotOnly) {
      hammers = [
        {
          key: hammersEvent.id,
          metagameType: 'hammer',
          event: hammersEvent,
          dataTestId: `metagames-${hammersEvent.typeName}-widget`,
        },
      ];
    }

    let puzzles: MetagameWidgetDataType[] = [];
    if (puzzleEvent && !puzzleEvent.isCrownSlotOnly) {
      puzzles = [
        {
          key: puzzleEvent.id,
          metagameType: 'puzzle',
          event: puzzleEvent,
          dataTestId: `metagames-${puzzleEvent.typeName}-widget`,
        },
      ];
    }

    let trails: MetagameWidgetDataType[] = [];
    if (trailEvent && !trailEvent.isCrownSlotOnly) {
      trails = [
        {
          key: trailEvent.id,
          metagameType: 'trail',
          event: trailEvent,
          dataTestId: `metagames-${trailEvent.typeName}-widget`,
        },
      ];
    }

    let plinko: MetagameWidgetDataType[] = [];
    if (plinkoEvent && !plinkoEvent.isCrownSlotOnly) {
      plinko = [
        {
          key: plinkoEvent.id,
          metagameType: 'plinko',
          event: plinkoEvent,
          dataTestId: `metagames-${plinkoEvent.typeName}-widget`,
        },
      ];
    }

    let roulette: MetagameWidgetDataType[] = [];
    if (rouletteEvent && !rouletteEvent.isCrownSlotOnly) {
      roulette = [
        {
          key: rouletteEvent.id,
          metagameType: 'roulette',
          event: rouletteEvent,
          dataTestId: `metagames-${rouletteEvent.typeName}-widget`,
        },
      ];
    }

    return {
      hammer: hammers,
      puzzle: puzzles,
      trail: trails,
      plinko: plinko,
      roulette: roulette,
    };
  }, [hammersEvent, plinkoEvent, puzzleEvent, rouletteEvent, trailEvent]);

  return useMemo(
    () =>
      Object.values(metagameWidgets)
        .flat()
        .map((data): WidgetEntry => ({ data, type: data.metagameType, id: data.key })),
    [metagameWidgets],
  );
}
